/**
 *  cst-cart-requests — the cart's request coordinator.
 *
 *  Design: docs/plans/2026-09-20-v8-cart-port-design.md — hunk 1 and "Request lifecycle state table".
 *
 *  window.CstCart = { change, refresh, post }. It owns the SCHEDULING and the TRANSPORT half of
 *  every cart lane: one 300 ms debounce timer per line key, one global FIFO across both cart
 *  surfaces, an AbortController with a 10 s deadline, a catch, HTTP-status classification and a
 *  paint generation. It owns NO DOM: the tag is a blocking <script src> in <head>
 *  (layout/theme.liquid), where document.body is still null, so every caller does its own
 *  painting, spinner clearing and event dispatching from the resolved result.
 *
 *  Addressing (A2-1, A2-18): quantity changes and removals POST {id: <line item key>} to
 *  theme.routes.cart_change_url — /cart/change, NO .js suffix — with Accept: application/json.
 *  That pairing is what selects the JSON error contract; /cart/change.js answers a DIFFERENT body
 *  shape and no `errors` key. Do not "modernise" the endpoint.
 *
 *  Lanes are discriminated by HTTP STATUS, never by body keys (A2-18): 2xx = applied, 400 = the
 *  target is gone (silent), 422 = a refusal AND a partial commit, anything else takes the silent
 *  lane. An abort, a network failure or an unparsable body is NEVER proof the server did nothing,
 *  so no mutation is ever retried — every one of those lanes ends in one idempotent reconcile
 *  (GET ?sections= + GET /cart.js) whose fresh cart and fresh section HTML come back in the same
 *  result, so a caller never has to re-fetch or guess.
 *
 *  ── THE RESULT CONTRACT ──────────────────────────────────────────────────────────────────────
 *
 *  Every method resolves — never rejects, never resolves undefined — with exactly this shape:
 *
 *    { ok, status, reason, parsedState, refusalText, sections, cart,
 *      staleGeneration, reconciled, reconcileFailed }
 *
 *  THE OUTCOMES. A result that a `change()` or `refresh()` caller receives is exactly ONE of these
 *  five, and the four non-clean ones are mutually exclusive — there is no partial state to branch
 *  on, and no result reaches a caller in none of them:
 *
 *    clean            `ok:true` with `reconciled:false`. Paint `sections` (null only when no
 *                     section list was asked for) and emit the settle yourself.
 *    reconciled       `reconciled:true`. Paint `sections`, emit exactly ONE
 *                     line-item:change:end {quantity: null, cart}. `ok` still names the ORIGINATING
 *                     lane, so a 422 that reconciled is `ok:false` with `reason:'refused'`.
 *    reconcileFailed  `reconcileFailed:true`. Paint nothing, emit nothing, leave an explicit retry
 *                     path (never a retry loop). Forces `ok:false`.
 *    superseded       `reason:'superseded'` with NEITHER flag set. Paint nothing, emit nothing,
 *                     clear your own spinner. Either it never reached the network, or the cart
 *                     moved under it and the traffic that moved it paints all three targets
 *                     itself. Always `ok:false`.
 *    invalid          `reason:'invalid'`. The call itself was unusable; nothing was sent.
 *
 *  BRANCH IN THAT ORDER — `reconciled`, then `reconcileFailed`, then `reason` — because `reason`
 *  names the ORIGINATING lane, not the outcome: a stale 2xx whose reconcile then completed is
 *  `reason:'superseded'` AND `reconciled:true`, and the reconcile is what the caller acts on. The
 *  two flags are never both set, and when NEITHER is set `reason` is exactly 'superseded' or
 *  'invalid' — there is no fifth row. `ok:true` therefore always carries a `sections` map to paint,
 *  or no section list was asked for. `post()` is NOT in this set — it has no reconcile at all; see
 *  its paragraph below.
 *
 *    ok               the request succeeded AND its own result is still usable: 2xx, a parsable
 *                     body, the paint generation did not move under it, and — on `change()`, when a
 *                     section list was asked for — the response carried a body for EVERY id in that
 *                     list. A 2xx short of one body is not the clean lane; it reconciles. (`post()`
 *                     has no reconcile to fall through to, so it classifies the map it was given
 *                     and its caller guards each body — see its paragraph.) `ok:false` alone never
 *                     means "nothing to paint" — read `reconciled`. `reconcileFailed` and
 *                     `reason:'superseded'` each force `ok:false`.
 *    status           the HTTP status actually read; 0 when no response was read at all (network
 *                     failure, or an abort before the headers). A response the server committed
 *                     and the client stopped reading keeps its REAL status with reason 'abort'.
 *                     `status` and `refusalText` always name the ORIGINATING lane, even when the
 *                     result is later relabelled 'superseded' by what happened to its reconcile.
 *    reason           exactly one of:
 *                       'ok'          2xx, parsable, generation current
 *                       'stale'       400 (target gone) or any other unhandled non-2xx. No message
 *                       'refused'     422: a refusal AND a partial commit; `refusalText` is set
 *                       'abort'       the 10 s deadline fired. NEVER retried — it may have committed
 *                       'network'     fetch rejected (offline, unreadable 5xx)
 *                       'parse'       the body was not usable as a cart: either it was not JSON at
 *                                     all (an HTML error page), or — on the reconcile's /cart.js —
 *                                     it parsed but carried no `items` array
 *                       'superseded'  paint nothing and emit nothing from this result. Three ways
 *                                     in: (a) it never reached the network — a newer intent for the
 *                                     same line replaced it in the timer or in the queue, or
 *                                     `pagehide` cancelled it; (b) it was sent and its own markup
 *                                     was dropped because the cart moved under it, and its
 *                                     reconcile then carried the truth back; (c) the cart moved
 *                                     AGAIN while that reconcile's GET pair was in flight, so even
 *                                     the reconcile is older than the screen — the traffic that
 *                                     moved the generation paints all three targets itself, so this
 *                                     result is a no-op and there is deliberately no second
 *                                     reconcile
 *                       'invalid'     the call itself was unusable (`change()` with no line key).
 *                                     Nothing was sent
 *    parsedState      the parsed body of THIS request (the error body on a failure lane), null when
 *                     nothing parsable came back. Its `sections` key is deleted whenever the
 *                     coordinator drops the markup, so it is never a back door to stale HTML, and
 *                     it is null outright on the `reconcileFailed` and `superseded` outcomes. Once
 *                     `reconciled` is set it is still THIS request's own body — one mutation older
 *                     than the truth just handed back — and NEVER the current cart: use `cart`.
 *    refusalText      422 only: errors (when a string) || description || message (A2-18); '' else.
 *    sections         the section-HTML map to paint, or null. Non-null whenever `ok` is true and a
 *                     section list was asked for, and whenever `reconciled` is set and a section
 *                     list was asked for. On the clean lane every id that was asked for is present
 *                     with a truthy body; a reconcile's map is whatever the sections GET returned,
 *                     so a caller still guards each target's body before painting (the design's
 *                     "a mounted target's section body is missing or null" row). It is NEVER markup
 *                     the coordinator has marked stale, and it is null on `reconcileFailed` and on
 *                     `superseded`.
 *    cart             the fresh /cart.js body — present ONLY when a reconcile completed.
 *    staleGeneration  the cart moved (`cart:item-added`, or another settle painted) while this
 *                     request — or its reconcile — was in flight, so its own markup was discarded.
 *    reconciled       a reconcile completed: paint `sections`, emit exactly ONE
 *                     line-item:change:end {quantity: null, cart}.
 *    reconcileFailed  the reconcile did not complete: paint nothing, emit NO line-item:change:end.
 *                     All-or-nothing — a half-answered pair (section HTML but no cart, or a cart
 *                     but no HTML) sets this, and `sections`, `cart` AND `parsedState` are all null
 *                     on this lane. There is no third, partial state to branch on. A reconcile that
 *                     was SUPERSEDED is not a failed one and sets neither flag: see 'superseded'.
 *
 *  Inputs: `sections` is an ARRAY of section ids — every caller builds it with `.map()`. A comma
 *  string is accepted and split verbatim (no trimming, no filtering), and `key` is a Shopify line
 *  item key (`<variant>:<hash>`) rejected only when undefined, null or ''. Neither input is
 *  normalised any further: a caller owns what it sends.
 *
 *  Per method:
 *    change({key, quantity, sections, sectionsUrl})
 *      300 ms debounce keyed by line key, then the FIFO; quantity '0' skips the debounce. Resolves
 *      'invalid' (nothing sent) when `key` is missing or ''; 'superseded' when a newer intent for
 *      the same line replaced it before it started, or when its own markup went stale and its
 *      reconcile was superseded too. Every lane except the clean one ends in a reconcile, so
 *      `sections` AND `cart` arrive in the SAME result and no caller re-fetches. With NO section
 *      list nothing is requested and a 2xx does not reconcile: `{ok:true, reason:'ok',
 *      sections:null, cart:null}`.
 *    refresh({sections, sectionsUrl})
 *      the reconcile alone, inside the FIFO so it can never paint across an in-flight mutation.
 *      Resolves `reconciled` (with `sections` + `cart`), `reconcileFailed`, or 'superseded' when
 *      the generation moved while its own GET pair was in flight.
 *    post({url, payload, sections, sectionsUrl})
 *      the callers' own routes (discounts, notes, adds): the same transport and the same classified
 *      shape with NO debounce, NO FIFO and NO reconcile — it never sets `cart`, `reconciled` or
 *      `reconcileFailed`, and on `staleGeneration` it hands back `sections: null`. That caller
 *      paints nothing from such a result: it either calls `refresh()` once for the server truth or
 *      waits for the next settle. Recovery is the caller's, because post() has no reconcile.
 *
 *  Deadline budget: ONE AbortController + ONE 10 s deadline per request, and the reconcile's two
 *  GETs are issued in the same tick under a SINGLE shared 10 s deadline. So the worst case for a
 *  mutation that has to reconcile is 20 s (10 s mutation + 10 s reconcile pair) — and that is the
 *  longest one job can hold the FIFO.
 */
(function () {
  // Evaluating this file twice must not re-register listeners or fork the queue.
  if (window.CstCart) return;

  const DEBOUNCE_MS = 300;
  const DEADLINE_MS = 10000;

  // Pending per-line-key debounce timers: key -> { id, resolve }.
  const timers = new Map();
  // One global FIFO across both surfaces: `sections` responses are whole-DOM and cannot be merged.
  const queue = [];
  let running = false;
  // Paint generation. Bumped by cart:item-added (adds stay outside the FIFO) and by every settle
  // that hands paintable sections back. A settle whose generation moved under it must not paint
  // markup computed before the newer change landed.
  let gen = 0;

  // Routes are read at CALL time, never at evaluation time: this file is the first tag after the
  // window.theme block and must not depend on anything having run before it.
  const routes = () => (window.theme && window.theme.routes) || {};

  const sectionNames = (sections) =>
    Array.isArray(sections) ? sections.slice() : typeof sections === 'string' && sections ? sections.split(',') : [];

  const sectionsUrl = (options) => (options && options.sectionsUrl) || window.location.pathname;

  // The one resolved shape. Field by field, and reason by reason, in THE RESULT CONTRACT above —
  // that block is the contract; this factory is only its default row.
  const result = (fields) =>
    Object.assign(
      {
        ok: false,
        status: 0,
        reason: 'ok',
        parsedState: null,
        refusalText: '',
        sections: null,
        cart: null,
        staleGeneration: false,
        reconciled: false,
        reconcileFailed: false
      },
      fields
    );

  // A 422 body has TWO shapes on the same endpoint (A2-18): {"errors":"Only 20 items…"} and
  // {"status":422,"message":"Cart Error","description":"Cannot find variant"}. `errors` is also an
  // OBJECT on some routes, so only a string counts.
  const refusalText = (parsed) => {
    if (!parsed) return '';
    if (typeof parsed.errors === 'string') return parsed.errors;
    if (typeof parsed.description === 'string') return parsed.description;
    if (typeof parsed.message === 'string') return parsed.message;
    return '';
  };

  const classify = (status, parsed) => {
    if (status >= 200 && status < 300) {
      return result({
        ok: true,
        status,
        reason: 'ok',
        parsedState: parsed,
        sections: parsed && typeof parsed.sections === 'object' && parsed.sections ? parsed.sections : null
      });
    }
    if (status === 422) {
      return result({ status, reason: 'refused', parsedState: parsed, refusalText: refusalText(parsed) });
    }
    // 400 = no valid id: the target is gone. Any other non-2xx takes the same silent lane.
    return result({ status, reason: 'stale', parsedState: parsed });
  };

  // Bounded transport. Resolves a result, never rejects, and always clears its own deadline timer.
  // `sharedSignal` lets several requests issued in the same tick (the reconcile pair) run under ONE
  // deadline instead of one each: when it is supplied this call owns no timer and clears none.
  const send = (url, options, sharedSignal) => {
    const controller = sharedSignal ? null : new AbortController();
    const deadline = controller ? setTimeout(() => controller.abort(), DEADLINE_MS) : null;
    const clearDeadline = () => {
      if (deadline !== null) clearTimeout(deadline);
    };
    let status = 0;
    return fetch(url, Object.assign({ signal: sharedSignal || controller.signal }, options))
      .then((response) => {
        status = response.status;
        return response.text();
      })
      .then((text) => {
        clearDeadline();
        let parsed;
        try {
          parsed = JSON.parse(text);
        } catch (e) {
          // An HTML error page, not JSON: the server may still have committed.
          return result({ status, reason: 'parse' });
        }
        return classify(status, parsed);
      })
      .catch((error) => {
        clearDeadline();
        // `status` is whatever was read before the failure: a response the server committed and the
        // client then stopped reading arrives here with its real status and reason 'abort'.
        return result({ status, reason: error && error.name === 'AbortError' ? 'abort' : 'network' });
      });
  };

  // The idempotent recovery GET pair. Section HTML re-paints the surfaces; /cart.js is the payload
  // the single line-item:change:end carries, so the rewards bar and the gift controller re-settle
  // without a round-trip of their own. Never a retried mutation.
  const reconcile = (options) => {
    const list = sectionNames(options && options.sections);
    const base = sectionsUrl(options);
    // MPC: D1 R3-O1 — the pair is issued AT this generation. A reconcile is a paint hand-off like
    // any other, so it owes the same staleness check every mutation makes: without this an add (or
    // a post() settle) that lands between the dispatch and the responses was invisible here and the
    // pre-add pair went back as reconciled:true with pre-add sections and a pre-add cart.
    const genAtStart = gen;
    // ONE controller and ONE 10 s deadline for the PAIR: both GETs are issued in this same tick and
    // share the budget, so a recovery can never cost more than a single deadline (header: 20 s is
    // the worst case for mutation + reconcile, and it is the most the FIFO can be held).
    const pair = new AbortController();
    const deadline = setTimeout(() => pair.abort(), DEADLINE_MS);
    // NO Accept header on the sections GET (observed on the live store, 2026-09-20): with
    // `Accept: application/json`, GET /cart?sections=… is content-negotiated into the CART JSON and
    // the sections parameter is ignored, so the "section map" came back as a cart and nothing
    // painted. The Section Rendering API answers JSON on its own; stock v8 sends no header either.
    const sectionsRequest = list.length
      ? send(`${base}${base.indexOf('?') === -1 ? '?' : '&'}sections=${list.join(',')}`, {}, pair.signal)
      : Promise.resolve(null);
    const cartRequest = send(
      `${routes().cart_url || '/cart'}.js`,
      { headers: { Accept: 'application/json' } },
      pair.signal
    );

    return Promise.all([sectionsRequest, cartRequest]).then((responses) => {
      clearTimeout(deadline);
      const sectionsResult = responses[0];
      const cartResult = responses[1];
      // MPC: D1 R3-O1 — the cart moved while the pair was in flight, so even this fresh-looking
      // pair predates the screen. That traffic paints all three targets and emits its own settle
      // (assets/product.js renders THEN dispatches cart:item-added), so the honest answer is the
      // superseded no-op: nothing to paint, nothing to emit, no generation bump — and deliberately
      // NO second reconcile, which would be a retry loop against whatever keeps moving the cart.
      if (gen !== genAtStart) {
        return result({ status: cartResult.status, reason: 'superseded', staleGeneration: true });
      }
      // A section map is only a section map if it is keyed by the ids that were asked for (a body
      // may still be null — the caller skips that target). Anything else is not paintable.
      const map = sectionsResult && sectionsResult.ok ? sectionsResult.parsedState : null;
      const sections = map && typeof map === 'object' && list.every((name) => name in map) ? map : null;
      const parsedCart = cartResult.ok ? cartResult.parsedState : null;
      const cart = parsedCart && Array.isArray(parsedCart.items) ? parsedCart : null;
      const done = !!cart && (!list.length || !!sections);
      let reason = 'ok';
      if (!done) {
        if (cartResult.reason !== 'ok') reason = cartResult.reason;
        else if (sectionsResult && sectionsResult.reason !== 'ok') reason = sectionsResult.reason;
        else reason = 'parse';
      }
      // A completed reconcile IS a completed paint hand-off, so it moves the generation exactly like
      // a clean mutation does. Without this a request that started before the reconcile is not told
      // its markup is older than the truth just handed to the caller.
      if (done) gen += 1;
      // A reconcile is all-or-nothing. A half-answered pair (fresh section HTML but no cart, or a
      // cart with no HTML) is NOT a paintable truth: the design's "Reconciliation itself fails" row
      // paints none and emits no `:end`, and a paint with no `:end` leaves the gift controller, the
      // rewards bar and destash-restriction settled against DOM that was just replaced. So all
      // three carriers — `sections`, `cart` and the `parsedState` shadow of the cart — are withheld
      // unless `done`, exactly as the contract above states.
      return result({
        ok: done,
        status: cartResult.status,
        reason,
        parsedState: done ? cart : null,
        sections: done ? sections : null,
        cart: done ? cart : null,
        reconciled: done,
        reconcileFailed: !done
      });
    });
  };

  // Failure and stale-generation lanes keep their own status/reason/refusalText and take the
  // reconcile's server truth as what to paint. The reconcile's OUTCOME is what decides which of the
  // contract's four non-clean rows this result lands in — the originating lane only names it.
  const withReconcile = (res, options) =>
    reconcile(options).then((rec) => {
      res.sections = rec.sections;
      res.cart = rec.cart;
      res.reconciled = rec.reconciled;
      res.reconcileFailed = rec.reconcileFailed;
      if (rec.reconcileFailed) {
        // MPC: D1 R3-O2 — a 2xx whose reconcile failed has nothing usable left, so it must not
        // still read `ok:true`: a caller branching on `ok` would paint a null `sections` map and
        // emit a settle the reconcileFailed row forbids. `reason` is left alone so the originating
        // lane keeps its name.
        res.ok = false;
        // MPC: D1 R3-O4 — and the same withholding the reconcile applies to its own carriers
        // applies to the mutation's body: on this lane `parsedState` was either the error body or
        // a whole cart one mutation out of date, which is exactly what a ported settle would
        // dispatch as `line-item:change:end {cart: parsedState}`. status/reason/refusalText carry
        // everything this lane actually owes the caller.
        res.parsedState = null;
      }
      if (rec.staleGeneration) {
        // MPC: D1 R3-O1 — the reconcile was superseded by other traffic, so this result is the
        // superseded no-op: neither `reconciled` nor `reconcileFailed` is set, and the contract's
        // 'superseded' row is what the caller branches on. `status` and `refusalText` still name
        // the originating lane.
        res.ok = false;
        res.staleGeneration = true;
        res.reason = 'superseded';
        res.parsedState = null;
      }
      return res;
    });

  // One in flight at a time. A job may never wedge the queue: the lock is released and the next
  // job started whatever the job does, and a caller's own handler runs a microtask later, so an
  // exception inside it cannot reach this chain.
  const drain = () => {
    if (running) return;
    const entry = queue.shift();
    if (!entry) return;
    running = true;
    let settled;
    try {
      settled = entry.job();
    } catch (e) {
      // MPC: D1 R3-O6 — `reconcileFailed` on both catch-alls. A job that threw reached no reconcile
      // at all, so the contract's reconcileFailed row is exactly true of it (paint nothing, emit
      // nothing, explicit retry path) — and without the flag the caller would get a result in NONE
      // of the four non-clean rows.
      settled = Promise.resolve(result({ reason: 'network', reconcileFailed: true }));
    }
    Promise.resolve(settled)
      .catch(() => result({ reason: 'network', reconcileFailed: true }))
      .then((res) => {
        running = false;
        entry.resolve(res);
        drain();
      });
  };

  // Queued entries carry their line key so a newer intent for the same line can drop them.
  const enqueue = (job, key) =>
    new Promise((resolve) => {
      queue.push({ job, resolve, key });
      drain();
    });

  // Coalescing is not only about timers: drain() SHIFTS an entry before running it, so anything
  // still in the queue has never touched the network and can be dropped for free. Keyless entries
  // (refresh) are never swept.
  const supersedeQueued = (key) => {
    if (key === undefined || key === null) return;
    for (let i = queue.length - 1; i >= 0; i -= 1) {
      if (queue[i].key === key) queue.splice(i, 1)[0].resolve(result({ reason: 'superseded' }));
    }
  };

  // A result never exposes markup the coordinator has marked stale — not at the top level, and not
  // through the shadow copy inside parsedState, which is exactly the key the stock paint sites read
  // (assets/cart.js, assets/app.js, assets/product.js all read parsedState.sections[...]).
  const dropShadowSections = (res) => {
    if (res.parsedState && res.parsedState.sections) {
      res.parsedState = Object.assign({}, res.parsedState);
      delete res.parsedState.sections;
    }
  };

  const mutate = (options) => {
    const genAtSend = gen;
    const list = sectionNames(options.sections);
    const payload = { id: options.key, quantity: options.quantity };
    // No section list asked for means no section render server-side AND no recovery GET for markup
    // nobody wants: a plain 2xx is then the clean lane with `sections: null`.
    if (list.length) {
      payload.sections = list;
      payload.sections_url = sectionsUrl(options);
    }
    return send(routes().cart_change_url || '/cart/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then((res) => {
      // MPC: D1 R3S-2 — the clean lane needs EVERY body that was asked for, not merely some
      // `sections` object. Shopify answers a section id it cannot render with the key present and
      // the body null (verified: GET /cart?sections=…,not-a-real-section-xyz → 200, that key null),
      // and a short map used to take the clean lane: generation bumped, no reconcile, and the
      // caller handed a map missing a target it asked for with no signal at all. An incomplete map
      // now falls through to the existing reconcile path — no new lane.
      const full = !!res.sections && list.every((name) => res.sections[name]);
      if (res.ok && (full || !list.length) && genAtSend === gen) {
        gen += 1;
        return res;
      }
      res.staleGeneration = genAtSend !== gen;
      // A stale 2xx must not read like the clean settle row: its own markup is dropped, so it is a
      // superseded result and the caller paints from the reconcile instead. A non-2xx keeps its own
      // lane — the 422 refusal text is still owed to the shopper.
      if (res.staleGeneration && res.ok) {
        res.ok = false;
        res.reason = 'superseded';
      }
      dropShadowSections(res);
      return withReconcile(res, options);
    });
  };

  /**
   * change({key, quantity, sections, sectionsUrl}) — one debounce timer per line key.
   * A second edit to the same key inside the window replaces the first (last write wins) and the
   * superseded promise resolves `superseded` — it never ran, so its caller must paint nothing.
   * quantity 0 CANCELS any pending timer AND any queued entry for that key and is sent immediately,
   * so a queued old quantity can never recreate a removed line: exactly one mutation. An in-flight
   * POST is never aborted by a newer intent; the newer one waits its turn in the FIFO.
   */
  const change = (options) => {
    const opts = options || {};
    const key = opts.key;
    // No line key is not a request: JSON.stringify would drop `id` entirely and the server answers
    // 400 "Parameter Missing or Invalid" (A2-18), while every keyless call would silently share one
    // debounce timer. Fail locally, at zero requests, with a reason a caller can log.
    if (key === undefined || key === null || key === '') {
      return Promise.resolve(result({ reason: 'invalid' }));
    }
    const pending = timers.get(key);
    if (pending) {
      clearTimeout(pending.id);
      timers.delete(key);
      pending.resolve(result({ reason: 'superseded' }));
    }
    // The other half of coalescing: an entry whose timer already fired is waiting in the FIFO and
    // has NOT started, so a newer intent for the same line drops it too — that is what keeps N
    // rapid edits at one POST per settle, and a removal at exactly one mutation.
    supersedeQueued(key);
    if (String(opts.quantity) === '0') return enqueue(() => mutate(opts), key);
    return new Promise((resolve) => {
      const id = setTimeout(() => {
        timers.delete(key);
        enqueue(() => mutate(opts), key).then(resolve);
      }, DEBOUNCE_MS);
      timers.set(key, { id, resolve });
    });
  };

  /**
   * refresh({sections, sectionsUrl}) — the reconcile on its own, in the FIFO so it can never paint
   * across an in-flight mutation. Resolves `reconciled` with `sections` to paint and `cart` to
   * carry in ONE line-item:change:end, or `reconcileFailed` — after which the caller emits none.
   */
  const refresh = (options) => enqueue(() => reconcile(options || {}));

  /**
   * post({url, payload, sections, sectionsUrl}) — the callers' own routes (discounts, notes,
   * adds): same bounded transport and the same classified result, without the key debounce, the
   * FIFO or the automatic reconcile. `sections`/`sections_url` are merged into the payload when a
   * section list is supplied.
   */
  const post = (options) => {
    const opts = options || {};
    const payload = Object.assign({}, opts.payload);
    const list = sectionNames(opts.sections);
    if (list.length) {
      payload.sections = list;
      payload.sections_url = sectionsUrl(opts);
    }
    const genAtSend = gen;
    return send(opts.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then((res) => {
      res.staleGeneration = genAtSend !== gen;
      if (res.staleGeneration) {
        // post() does not reconcile, so there is nothing fresh to swap in: hand back no markup at
        // all rather than HTML computed before the newer change landed, and label it superseded so
        // it cannot be mistaken for the clean lane.
        res.sections = null;
        dropShadowSections(res);
        if (res.ok) {
          res.ok = false;
          res.reason = 'superseded';
        }
      } else if (res.ok && res.sections) {
        gen += 1;
      }
      return res;
    });
  };

  // Adds stay outside the FIFO (they already catch, finally and guard their own repaint), so the
  // generation is how a settle learns the cart moved under it.
  document.addEventListener('cart:item-added', () => {
    gen += 1;
  });

  // A debounce timer is frozen, not dropped, when a page enters the back/forward cache: without
  // this a restored page would send a quantity the shopper typed before navigating away.
  // Timers ONLY, deliberately: an entry already waiting in the FIFO has cleared its 300 ms window,
  // which is the same level of shopper commitment as the in-flight POST that is never aborted, so
  // it is left to send. Accepted (manager ruling, review round 2) — not an oversight.
  window.addEventListener('pagehide', () => {
    timers.forEach((pending) => {
      clearTimeout(pending.id);
      pending.resolve(result({ reason: 'superseded' }));
    });
    timers.clear();
  });

  window.CstCart = { change, refresh, post };
})();
